import React, { useState, useEffect, createContext } from 'react'
import { useNavigate } from 'react-router-dom'
import axios, { AxiosResponse } from 'axios'
import moment from 'moment'

import { apiUrl } from '../../../globals'
import { BasicCourseData, CourseForParticipantResponse, ExamInfo } from '../types'
import { CoursePage, ExamQuestion } from '../../Course/types'
import { AllQuestionsAnsweredResponse, ExamResults, ParticipantCourseContextType, ParticipantCourseProviderProps, StartExamRequest, StartExamResponse } from './types'
import { notification } from 'antd'
  
export const ParticipantCourseContext = createContext<ParticipantCourseContextType>({
  hasCheckedLink: false,
  isIdentified: false,
  courseHasEndedError: false,
  invalidLinkError: false,
  basicCourseData: undefined,
  originalLinkForParticipants: '',
  coursePages: [],
  examInfo: undefined,
  logo: '',
  hasAgreedToStartExam: false,
  currentPage: 0,
  courseProgress: 0,
  isExamStarted: false,
  isStartExamModalVisible: false,
  displayWelcomeModal: false,
  hasSetWelcomeModalToNeverShowAgain: false,
  hasReachedLastPage: false,
  maxExamTriesExceeded: false,
  maxExamParticipantsExceeded: false,
  examAttemptId: null,
  examAttemptCount: 0,
  maxTries: 0,
  currentExamQuestion: undefined,
  examTimeRemaining: '',
  timeIsUp: false,
  allQuestionsAnswered: false,
  examResults: undefined,
  accessExam: async () => { return },
  setHasCheckedLink: () => undefined,
  setIsIdentified: () => undefined,
  setCourseHasEndedError: () => undefined,
  setInvalidLinkError: () => undefined,
  setBasicCourseData: () => undefined,
  setOriginalLinkForParticipants: () => undefined,
  setCoursePages: () => undefined,
  setExamInfo: () => undefined,
  setLogo: () => undefined,
  setHasAgreedToStartExam: () => undefined,
  setCurrentPage: () => undefined,
  setCourseProgress: () => undefined,
  setIsExamStarted: () => undefined,
  setIsStartExamModalVisible: () => undefined,
  setDisplayWelcomeModal: () => undefined,
  calculateCourseProgress: () => undefined,
  startExam: () => undefined,
  resumeExam: async () => { return },
  setCurrentExamQuestion: () => undefined,
  submitAnswer: async () => { return },
})

export const ParticipantCourseProvider = ({ children }: ParticipantCourseProviderProps) => {
  const navigate = useNavigate()

  const [ hasCheckedLink, setHasCheckedLink ] = useState<boolean>(false)
  const [ isIdentified, setIsIdentified ] = useState<boolean>(false)
  const [ courseHasEndedError, setCourseHasEndedError ] = useState<boolean>(false)
  const [ invalidLinkError, setInvalidLinkError ] = useState<boolean>(false)

  const [ basicCourseData, setBasicCourseData ] = useState<BasicCourseData | undefined>(undefined)
  const [ coursePages, setCoursePages ] = useState<Array<CoursePage>>([])
  const [ originalLinkForParticipants, setOriginalLinkForParticipants ] = useState<string>('')

  const [ displayWelcomeModal, setDisplayWelcomeModal ] = useState<boolean>(true)
  const hasSetWelcomeModalToNeverShowAgain = localStorage.getItem('neverDisplayWelcomeModal') === 'true' ? true : false

  const [ examInfo, setExamInfo ] = useState<ExamInfo>()
  const [ logo, setLogo ] = useState<string>('')

  const [ currentPage, setCurrentPage ] = useState<number>(0)
  const hasReachedLastPage = currentPage === coursePages.length
  const [ courseProgress, setCourseProgress ] = useState<number>(0)

  const [ isStartExamModalVisible, setIsStartExamModalVisible ] = useState<boolean>(false)
  const [ hasAgreedToStartExam, setHasAgreedToStartExam ] = useState<boolean>(false)
  const [ isExamStarted, setIsExamStarted ] = useState<boolean>(false)
  const [ maxExamTriesExceeded, setMaxExamTriesExceeded ] = useState<boolean>(false)
  const [ maxExamParticipantsExceeded, setMaxExamParticipantsExceeded ] = useState<boolean>(false)
  const [ examAttemptId, setExamAttemptId ] = useState<number>(0)
  const [ examAttemptCount, setExamAttemptCount ] = useState<number>(0)
  const [ maxTries, setMaxTries ] = useState<number>(0)
  const [ currentExamQuestion, setCurrentExamQuestion ] = useState<ExamQuestion>()
  const [ examStartedAt, setExamStartedAt ] = useState<moment.Moment>()
  const [ examDuration, setExamDuration ] = useState<number>()
  const [ examTimeRemaining, setExamTimeRemaining ] = useState('')
  const [ timeIsUp, setTimeIsUp ] = useState<boolean>(false)

  const [ allQuestionsAnswered, setAllQuestionsAnswered ] = useState<boolean>(false)

  const [ examResults, setExamResults ] = useState<ExamResults>()

  useEffect(() => {
    calculateCourseProgress()
  }, [ currentPage, coursePages, courseProgress ])

  const calculateCourseProgress = () => {
    const totalPages = coursePages.length > 0 ? coursePages.length - 1 : 1
    setCourseProgress((currentPage / totalPages) * 100)
  }

  const formatTime = (time: number) => time < 10 ? '0' + time : time

  useEffect(() => {
    if (hasAgreedToStartExam) {
      setCoursePages([])
      setCurrentPage(0)
      setCourseProgress(0)
    }
  }, [ hasAgreedToStartExam ])

  useEffect(() => {
    if (!examStartedAt || !examDuration) return

    const endTime = moment(examStartedAt).add(examDuration, 'minutes')
    const intervalId = setInterval(() => {
      const now = moment()
      const diff = moment.duration(endTime.diff(now))
      const remaining = `${formatTime(diff.hours())}:${formatTime(diff.minutes())}:${formatTime(diff.seconds())}`

      setExamTimeRemaining(remaining)

      if (diff.asSeconds() <= 0) {
        clearInterval(intervalId)
        setTimeIsUp(true)
      }
    }, 1000)

    return () => clearInterval(intervalId)
  }, [ examStartedAt, examDuration ])

  const accessExam = async (url: string) => {
    axios.post(`${apiUrl}/access-exam`, { CourseLink: url })
      .then((res: AxiosResponse<CourseForParticipantResponse>) => {
        if (res.status === 200 && res.data) {
          const {
            minPercentageToPass,
            totalPoints,
            examDurationInMinutes,
            examValidityMonths,
            numberOfQuestions,
            pages,
            maxTries
          } = res.data

          setIsIdentified(true)
          setCoursePages(pages)
          setExamInfo({
            minPercentageToPass,
            totalPoints,
            examDurationInMinutes,
            examValidityMonths,
            numberOfQuestions,
            maxTries
          })
          setLogo(res.data.logo)
          setBasicCourseData({
            courseId: res.data.courseId,
            courseName: res.data.courseName,
            description: res.data.description,
            numberOfQuestions: res.data.numberOfQuestions,
            pages: res.data.pages,
            minPercentageToPass: res.data.minPercentageToPass,
            totalPoints: res.data.totalPoints,
            examDurationInMinutes: res.data.examDurationInMinutes,
            maxTries: res.data.maxTries
          })
        }
      }).catch((err) => {
        setInvalidLinkError(true)
        console.log(err)
      })
  }

  const startExam = ({ firstName, lastName, birthDate, originalLinkForParticipants, email}: StartExamRequest) => {
    axios.post(`${apiUrl}/start-exam`, {
      courseId: basicCourseData?.courseId,
      firstName,
      lastName,
      birthDate,
      originalLinkForParticipants,
      email
    }).then((res: AxiosResponse<StartExamResponse>) => {
      navigate(`/exams/${originalLinkForParticipants}/attempt/${res.data.examAttemptId}`)

      setIsStartExamModalVisible(false)
      setHasAgreedToStartExam(true)
      setIsExamStarted(true)

      setCurrentExamQuestion(res.data.examQuestion)
      setExamStartedAt(moment(res.data.examStartedAt))
      setExamDuration(res.data.examDuration)
      setExamAttemptId(res.data.examAttemptId)
      setExamAttemptCount(res.data.examAttemptCount + 1)
      setMaxTries(res.data.maxTries)
    }).catch((err) => {
      if (err.response.data === 'Maximum number of tries exceeded') {
        setMaxExamTriesExceeded(true)
      }

      if (err.response.data === 'Maximum number of participants exceeded') {
        setMaxExamParticipantsExceeded(true)
      }
    })
  }

  const resumeExam = async (examAttemptId: number) => {
    await axios.post(`${apiUrl}/resume-exam`, { examAttemptId })
      .then((res: AxiosResponse<StartExamResponse>) => {
        setIsStartExamModalVisible(false)
        setHasAgreedToStartExam(true)
        setIsExamStarted(true)

        setCurrentExamQuestion(res.data.examQuestion)
        setExamStartedAt(moment(res.data.examStartedAt))
        setExamDuration(res.data.examDuration)
        setExamAttemptId(res.data.examAttemptId)
        setExamAttemptCount(res.data.examAttemptCount) // don't increment because it's a resume (so examAttempt isn't null in the db)
        setMaxTries(res.data.maxTries)
      }).catch((err) => {
        if (err.response.data === 'Maximum number of tries exceeded') {
          setMaxExamTriesExceeded(true)
        }

        if (err.response.data === 'Maximum number of participants exceeded') {
          setMaxExamParticipantsExceeded(true)
        }

        if (err.response.data === 'Exam already finished or time has expired') {
          setTimeIsUp(true)
        }
      })
  }

  const submitAnswer = async (answerIds: Array<number>) => {
    await axios.post(`${apiUrl}/submit-answer`, {
      examAttemptId: examAttemptId,
      questionId: currentExamQuestion?.id,
      answerIds,
    }).then((res: AxiosResponse<ExamQuestion | AllQuestionsAnsweredResponse>) => {
      if ('allQuestionsAnswered' in res.data && res.data.allQuestionsAnswered === true) {
        setAllQuestionsAnswered(true)
      } else {
        if ('id' in res.data) {
          setCurrentExamQuestion(res.data)
        }
      }

      return Promise.resolve()
    }).catch((err) => {
      if (err.response.data === 'Exam time exceeded.') {
        notification.error({
          message: 'Die Zeit ist abgelaufen.',
          description: 'Sie können den Test nicht mehr fortsetzen.',
          placement: 'bottomRight',
        })
      }

      return Promise.reject()
    })
  }

  return (
    <ParticipantCourseContext.Provider value={{
      accessExam,
      isIdentified,
      setIsIdentified,
      hasCheckedLink,
      setHasCheckedLink,
      courseHasEndedError,
      setCourseHasEndedError,
      invalidLinkError,
      setInvalidLinkError,
      basicCourseData,
      setBasicCourseData,
      originalLinkForParticipants,
      setOriginalLinkForParticipants,
      coursePages,
      setCoursePages,
      examInfo,
      setExamInfo,
      logo,
      setLogo,
      hasAgreedToStartExam,
      setHasAgreedToStartExam,
      currentPage,
      setCurrentPage,
      courseProgress,
      setCourseProgress,
      isExamStarted,
      setIsExamStarted,
      isStartExamModalVisible,
      setIsStartExamModalVisible,
      displayWelcomeModal,
      setDisplayWelcomeModal,
      hasSetWelcomeModalToNeverShowAgain,
      hasReachedLastPage,
      calculateCourseProgress,
      startExam,
      resumeExam,
      maxExamTriesExceeded,
      maxExamParticipantsExceeded,
      examAttemptId,
      examAttemptCount,
      maxTries,
      currentExamQuestion,
      setCurrentExamQuestion,
      examTimeRemaining,
      timeIsUp,
      submitAnswer,
      allQuestionsAnswered,
      examResults
    }}>
      {children}
    </ParticipantCourseContext.Provider>
  )
}

